import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';

import { HomeComponent } from './home.component';
import { LegacyHomeComponent } from '../legacy/legacy-home.component';
import { Shell } from '../shell/shell.service';
import { SearchComponent } from '../search/search.component';
import { IslandDetailsComponent } from '../island-details/island-details.component';
import { IslandsComponent } from '../islands/islands.component';
import { AboutUsComponent } from '../about-us/about-us.component';
import { ContactComponent } from '../contact/contact.component';
import { DownloadsComponent } from '../downloads/downloads.component';
import { FrequentlyquestionsComponent } from '../frequentlyquestions/frequentlyquestions.component';
import { TermsofuseComponent } from '../termsofuse/termsofuse.component';
import { PrivacypolicyComponent } from '../privacypolicy/privacypolicy.component';
import { GooglemapsComponent } from '../googlemaps/googlemaps.component';
import { ExploreSeychellesComponent } from '../explore-seychelles/explore-seychelles.component';
import { WishlistComponent } from '../wishlist/wishlist.component';
import { HighLightsDetailsComponent } from '../highLights/high-lights-details/high-lights-details.component';
import { HighLightsAllComponent } from '../highLights/high-lights-all/high-lights-all.component';
import { IslandsAllComponent } from '../islands-all/islands-all.component';
import { SpecialIntrestComponent } from '../special-intrest/special-intrest.component';
import { NotFoundComponent } from '../not-found/not-found.component';
import { CreoleComponent } from '../creole/creole.component';
import { TravelInfoComponent } from '../travel-info/travel-info.component';
import { AboutUsDetailComponent } from '../about-us/about-us-detail/about-us-detail.component';
import { TravelAdvisoryComponent } from '../travel-advisory/travel-advisory.component';
import { IslandGroupsComponent } from '../island-groups/island-groups.component';
import { AboutUsItemsComponent } from '../about-us/about-us-items/about-us-items.component';
import { CookiePolicyComponent } from '../cookie-policy/cookie-policy.component';
import { OpenweathermapComponent } from '../openweathermap/openweathermap.component';
import { TokencheckComponent } from '../tokencheck/tokencheck.component';
import { ChangePasswordComponent } from '../change-password/change-password.component';
import { FaqComponent } from '../faq/faq.component';
import { IslandsOverviewComponent } from '../islands-overview/islands-overview.component';
import { ExploreIslandComponent } from '../explore-island/explore-island.component';
import { UserActivationComponent } from '../user-activation/user-activation.component';

// NOTE: PlanyourvisitCategoriesComponent import removed - it is now
// lazy-loaded via loadChildren() below instead of imported directly.
// Do not re-add a static "component:" reference to it here, or it
// goes straight back into the eager bundle.
//
// NOTE: ListingDetailsComponent import removed for the same reason - it
// is now lazy-loaded via loadChildren() below. This route is hit on
// every product card click site-wide, so this was the highest-traffic
// component to extract so far.

const routes: Routes = [
  // Legacy route outside Shell wrapper - has its own header/footer
  {
    path: 'legacy',
    component: LegacyHomeComponent,
    data: { title: marker('Experience Seychelles | The Seychelles Islands') },
  },
  Shell.childRoutes([
    { path: '', redirectTo: '/home', pathMatch: 'full' },
    // New home route inside Shell wrapper for consistent header/footer
    {
      path: 'home',
      component: HomeComponent,
      data: { title: marker('Experience Seychelles | The Seychelles Islands') },
    },
    {
      path: 'app-search/:searchTerm',
      component: SearchComponent,
      data: { title: marker('Search | The Seychelles Islands') },
    },
    {
      path: 'aboutus/:selectedPage',
      component: AboutUsComponent,
      data: {
        title: marker('About Seychelles | The Seychelles Islands'),
        breadcrumbs: [
          {
            label: 'About Seychelles',
            url: '',
          },
        ],
      },
    },
    {
      path: 'about-us-items',
      component: AboutUsItemsComponent,
      data: { title: marker('About Seychelles | The Seychelles Islands') },
    },
    {
      path: 'about-us-detail',
      component: AboutUsDetailComponent,
      data: { title: marker('About Seychelles | The Seychelles Islands') },
    },
    {
      path: 'travelinfo/:selectedPage',
      component: TravelInfoComponent,
      data: {
        title: marker('Travel information, requirements and advisories | The Seychelles Islands'),
        breadcrumbs: [
          {
            label: 'Plan Your Visit',
            url: '',
          },
          {
            label: '{{selectedPage}}',
            url: '',
          },
        ],
      },
    },
    {
      path: 'listingdetails/:ProductId',
      loadChildren: () => import('../listing-details/listing-details.module').then((m) => m.ListingDetailsModule),
      data: {
        title: marker('app-listing-details'),
      },
    },
    {
      path: 'planyourvisit/:category',
      loadChildren: () =>
        import('../planyourvisit-categories/planyourvisit-categories.module').then(
          (m) => m.PlanyourvisitCategoriesModule
        ),
      data: {
        breadcrumbs: [
          {
            label: 'Plan Your Visit',
            url: '',
          },
          {
            label: '{{category}}',
            url: '',
          },
        ],
      },
    },
    {
      path: 'experience/:category',
      loadChildren: () =>
        import('../planyourvisit-categories/planyourvisit-categories.module').then(
          (m) => m.PlanyourvisitCategoriesModule
        ),
      data: {
        breadcrumbs: [
          {
            label: 'Experience',
            url: '',
          },
          {
            label: '{{category}}',
            url: '',
          },
        ],
      },
    },
    {
      path: 'explore-islands/overview',
      component: IslandsOverviewComponent,
    },
    {
      path: 'explore-island/:islandId',
      component: ExploreIslandComponent,
    },
    {
      path: 'island-details',
      component: IslandDetailsComponent,
      data: { title: marker('Islands | The Seychelles Islands') },
    },
    { path: 'islands', component: IslandsComponent, data: { title: marker('Islands | The Seychelles Islands') } },
    {
      path: 'islands-all',
      component: IslandsAllComponent,
      data: { title: marker('Islands | The Seychelles Islands') },
    },
    {
      path: 'island-groups',
      component: IslandGroupsComponent,
      data: { title: marker('Island Groups | The Seychelles Islands') },
    },
    { path: 'contactus', component: ContactComponent, data: { title: marker('Contact us | The Seychelles Islands') } },
    {
      path: 'downloads',
      component: DownloadsComponent,
      data: {
        title: marker('Downloads | The Seychelles Islands'),
        breadcrumbs: [
          {
            label: 'Downloads',
            url: '',
          },
        ],
      },
    },
    {
      path: 'app-frequentlyquestions',
      component: FrequentlyquestionsComponent,
      data: { title: marker('Frequently Asked Questions | The Seychelles Islands') },
    },
    {
      path: 'terms_of_use',
      component: TermsofuseComponent,
      data: { title: marker('Terms of Use | The Seychelles Islands') },
    },
    {
      path: 'faqs',
      component: FaqComponent,
      data: { title: marker('Faq | The Seychelles Islands') },
    },
    {
      path: 'privacy_policy',
      component: PrivacypolicyComponent,
      data: { title: marker('Privacy Policy | The Seychelles Islands') },
    },
    {
      path: 'openweathermap',
      component: OpenweathermapComponent,
      data: { title: marker('openweathermap | The Seychelles Islands') },
    },
    {
      path: 'user/reset/:user_id/:time/:token',
      component: TokencheckComponent,
      data: { title: marker('The Seychelles Islands') },
    },
    {
      path: 'reset-password',
      component: ChangePasswordComponent,
      data: { title: marker('Reset Password | The Seychelles') },
    },
    {
      path: 'app-googlemaps',
      component: GooglemapsComponent,
      data: { title: marker('Interactive Map | The Seychelles Islands') },
    },
    {
      path: 'explore-seychelles',
      component: ExploreSeychellesComponent,
      data: { title: marker('Interactive Map | The Seychelles Islands') },
    },
    {
      path: 'wishlist',
      component: WishlistComponent,
      data: { title: marker('My Wishlist | The Seychelles Islands') },
    },
    /*{
      path: 'blog-details/:view_node',
      component: HighLightsDetailsComponent,
      data: { title: marker('app-high-lights-details') },
    },*/
    {
      path: 'blog-details/:nodeid/:view_node',
      component: HighLightsDetailsComponent,
      data: { title: marker('app-high-lights-details') },
    },
    {
      path: 'blog-details/:nodeid/highlights/:view_node',
      component: HighLightsDetailsComponent,
      data: { title: marker('app-high-lights-details') },
    },
    {
      path: 'blog',
      component: HighLightsAllComponent,
      data: { title: marker('Our Stories - Blog | The Seychelles Islands') },
    },
    {
      path: 'special_interest',
      component: SpecialIntrestComponent,
      // data: { title: marker('special_interest') }
    },
    {
      path: 'special_interest/diving',
      loadChildren: () => import('../diving/diving.module').then((m) => m.DivingModule),
      data: {
        title: marker('Diving | The Seychelles Islands'),
        breadcrumbs: [
          {
            label: 'Special Interest',
            url: '',
          },
          {
            label: 'Diving',
            url: '',
          },
        ],
      },
    },
    {
      path: 'special_interest/culture',
      loadChildren: () => import('../culture/culture.module').then((m) => m.CultureModule),
      data: {
        title: marker('Culture | The Seychelles Islands'),
        breadcrumbs: [
          {
            label: 'Special Interest',
            url: '',
          },
          {
            label: 'Culture',
            url: '',
          },
        ],
      },
    },
    {
      path: 'culture/creole-rendezvous',
      component: CreoleComponent,
      data: {
        title: marker('Creole | The Seychelles Islands'),
        breadcrumbs: [
          {
            label: 'Culture',
            url: '',
          },
          {
            label: 'Creole Rendezvous',
            url: '',
          },
        ],
      },
    },
    {
      path: 'special_interest/fishing',
      loadChildren: () => import('../fishing/fishing.module').then((m) => m.FishingModule),
      data: {
        title: marker('Fishing | The Seychelles Islands'),
        breadcrumbs: [
          {
            label: 'Special Interest',
            url: '',
          },
          {
            label: 'Fishing',
            url: '',
          },
        ],
      },
    },
    {
      path: 'special_interest/romance',
      loadChildren: () => import('../romance/romance.module').then((m) => m.RomanceModule),
      data: {
        title: marker('Romance | The Seychelles Islands'),
        breadcrumbs: [
          {
            label: 'Special Interest',
            url: '',
          },
          {
            label: 'Romance',
            url: '',
          },
        ],
      },
    },
    {
      path: 'special_interest/sailing',
      loadChildren: () => import('../sailing/sailing.module').then((m) => m.SailingModule),
      data: {
        title: marker('Sailing | The Seychelles Islands'),
        breadcrumbs: [
          {
            label: 'Special Interest',
            url: '',
          },
          {
            label: 'Sailing',
            url: '',
          },
        ],
      },
    },
    {
      path: 'special_interest/nature',
      loadChildren: () => import('../general/general.module').then((m) => m.GeneralModule),
      data: {
        title: marker('Nature | The Seychelles Islands'),
        breadcrumbs: [
          {
            label: 'Special Interest',
            url: '',
          },
          {
            label: 'Nature',
            url: '',
          },
        ],
      },
    },
    {
      path: 'special_interest/workcation',
      loadChildren: () => import('../workcation/workcation.module').then((m) => m.WorkcationModule),
      data: {
        title: marker('Workcation | The Seychelles Islands'),
        breadcrumbs: [
          {
            label: 'Special Interest',
            url: '',
          },
          {
            label: 'Workcation',
            url: '',
          },
        ],
      },
    },
    {
      //path: 'special_interest/sustainability',
      path: 'special_interest/sustainable_seychelles',
      loadChildren: () => import('../sustainability/sustainability.module').then((m) => m.SustainabilityModule),
      data: {
        title: marker('Sustainability | The Seychelles Islands'),
        breadcrumbs: [
          {
            label: 'Special Interest',
            url: '',
          },
          {
            label: 'Sustainable Seychelles',
            url: '',
          },
        ],
      },
    },
    {
      path: 'travel-advisory',
      component: TravelAdvisoryComponent,
      data: { title: marker('Travel advisory | The Seychelles Islands') },
    },
    {
      path: 'cookie-policy',
      component: CookiePolicyComponent,
      data: { title: marker('Cookie Policy | The Seychelles Islands') },
    },
    {
      path: 'user-activation',
      component: UserActivationComponent,
      data: { title: marker('Verify Email | The Seychelles Islands') },
    },
    { path: '**', component: NotFoundComponent },
  ]),
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
  providers: [],
})
export class HomeRoutingModule {}
